import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { canAccessRouteByPermissions } from '@/config/routePermissions';
import {
  NAV_MODULE_DEFINITIONS,
  type NavModuleKey,
  getModuleLandingRoute,
} from '@/config/navigationModules';
import {
  getPermissionVisibleModules,
  mergeModuleVisibility,
  getFirstVisibleModuleLanding,
} from '@/utils/navigationVisibility';
import { getMyNavigationModules } from '@/services/navigationModules.service';
import { isPlatformSuperAdmin } from '@/utils/authUser';
import { useInstalledSolutionPacks } from '@/hooks/useInstalledSolutionPacks';
import { applyPackModuleGates } from '@/config/solutionPackNavGates';

export function useVisibleNavigationModules() {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = isPlatformSuperAdmin(user);
  const permissions = user?.permissions ?? [];
  const { installedPackKeys } = useInstalledSolutionPacks();

  const canAccessPath = (path: string) =>
    canAccessRouteByPermissions(path, permissions, isSuperAdmin);

  const permissionVisible = useMemo(
    () => getPermissionVisibleModules(canAccessPath, isSuperAdmin),
    [permissions, isSuperAdmin]
  );

  const operationalQuery = useQuery({
    queryKey: ['navigation-modules', user?.id, permissions.join('|')],
    queryFn: getMyNavigationModules,
    enabled: Boolean(user?.id) && !isSuperAdmin,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const visibleModules = useMemo(() => {
    const operationalMap = operationalQuery.data?.modules;
    const failOpen = operationalQuery.isError;
    const merged = mergeModuleVisibility(
      permissionVisible,
      operationalMap,
      isSuperAdmin,
      failOpen
    );
    // Pack gating applies to everyone including Super Admin
    return applyPackModuleGates(merged, installedPackKeys);
  }, [
    permissionVisible,
    operationalQuery.data,
    operationalQuery.isError,
    isSuperAdmin,
    installedPackKeys,
  ]);

  const visibleModuleList = useMemo(
    () => NAV_MODULE_DEFINITIONS.filter((m) => visibleModules.has(m.key)),
    [visibleModules]
  );

  const isModuleVisible = (key: NavModuleKey) => visibleModules.has(key);

  const defaultLanding = useMemo(
    () => getFirstVisibleModuleLanding(visibleModules),
    [visibleModules]
  );

  const getLandingRoute = (key: NavModuleKey) => getModuleLandingRoute(key, canAccessPath);

  /**
   * Treat as loading until operational modules are fetched (or failed).
   * Important: a disabled RQ query has isLoading=false — without this, merge sees
   * operationalMap=undefined and (before fail-open) hid sales/scm/mfg, causing
   * DashboardLayout to bounce Approval Inbox deep links to a blank/landing page.
   */
  const isLoading =
    !isSuperAdmin &&
    Boolean(user?.id) &&
    !operationalQuery.isFetched &&
    !operationalQuery.isError;

  return {
    visibleModules,
    visibleModuleList,
    isModuleVisible,
    defaultLanding,
    getLandingRoute,
    isLoading,
    isSuperAdmin,
  };
}
